import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate, useSearchParams, Link } from 'react-router-dom';
import api from '../utils/api';
import ChatSidebar from './ChatSidebar';
import MessageBubble from './MessageBubble';
import RightSidebar from './RightSidebar';
import DomainDetail from './DomainDetail';
import DomainDnsPanel from './DomainDnsPanel';
import SiteDetail from './SiteDetail';
import SitePanel from './SitePanel';
import DocumentDetail from './DocumentDetail';
import DocumentPanel from './DocumentPanel';
import '../styles/Chat.css';

function Chat() {
  const { conversationId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [currentConversation, setCurrentConversation] = useState(null);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(() => {
    // Initialize loading=true if ?service= param or pendingService exists
    // This prevents welcome screen flash before async work starts
    const params = new URLSearchParams(window.location.search);
    return !!(params.get('service') || sessionStorage.getItem('pendingService'));
  });
  const [sending, setSending] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(window.innerWidth > 768);
  const [rightSidebarOpen, setRightSidebarOpen] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const fileInputRef = useRef(null);
  const paymentHandledRef = useRef(false);
  const lastMessageIdRef = useRef(null);
  const [activeFolderId, setActiveFolderId] = useState(null);
  const [activeFolderName, setActiveFolderName] = useState(null);
  const [folders, setFolders] = useState([]);
  const [pendingFiles, setPendingFiles] = useState([]);
  const [fileRefreshTrigger, setFileRefreshTrigger] = useState(0);

  // Mode selector state
  const [mode, setMode] = useState(() => {
    try { return localStorage.getItem('tim-sidebar-mode') || 'conversations'; }
    catch { return 'conversations'; }
  });

  // Domain state
  const [domains, setDomains] = useState([]);
  const [domainsLoading, setDomainsLoading] = useState(false);
  const [selectedDomainId, setSelectedDomainId] = useState(null);
  const [dnsPanelOpen, setDnsPanelOpen] = useState(false);

  // Sites state
  const [sites, setSites] = useState([]);
  const [sitesLoading, setSitesLoading] = useState(false);
  const [selectedSiteId, setSelectedSiteId] = useState(null);
  const [sitePanelOpen, setSitePanelOpen] = useState(false);

  // Documents state
  const [documents, setDocuments] = useState([]);
  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [documentsError, setDocumentsError] = useState(null);
  const [selectedDocId, setSelectedDocId] = useState(null);
  const [docPanelOpen, setDocPanelOpen] = useState(false);

  // Load conversations and folders
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadConversations();
    loadFolders();
  }, []);

  // BUG 4 FIX: Handle payment redirect on mount
  useEffect(() => {
    const paymentStatus = searchParams.get('payment');
    const sessionId = searchParams.get('session_id');
    
    if (paymentStatus === 'success' && sessionId && !paymentHandledRef.current) {
      paymentHandledRef.current = true;
      handlePaymentSuccess(sessionId);
    } else if (paymentStatus === 'cancelled') {
      // Clear the query params using replaceState
      window.history.replaceState({}, '', window.location.pathname);
      // Show cancellation message to user
      const cancelledMsg = {
        id: 'temp_' + Date.now(),
        role: 'gatekeeper',
        content: "Payment was cancelled. Click 'Approve and Pay' when you're ready.",
        message_type: 'text',
        metadata: {},
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, cancelledMsg]);
    }

    // Handle hosting payment redirect
    const hostingPaymentStatus = searchParams.get('hosting_payment');
    if (hostingPaymentStatus === 'cancelled') {
      window.history.replaceState({}, '', window.location.pathname);
      const cancelledMsg = {
        id: 'temp_' + Date.now(),
        role: 'gatekeeper',
        content: "No worries — your free trial is still running. You can set up hosting anytime.",
        message_type: 'text',
        metadata: {},
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, cancelledMsg]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // W4.3: Handle template/service selection from Get Started flow
  useEffect(() => {
    const serviceId = searchParams.get('service');
    // Also check sessionStorage for auth redirect case
    const pendingService = sessionStorage.getItem('pendingService');
    const templateId = serviceId || pendingService;
    
    if (templateId && !conversationId) {
      // Show spinner IMMEDIATELY — before async work begins
      setLoading(true);
      
      // Clear the param/storage so refresh doesn't re-trigger
      if (serviceId) {
        window.history.replaceState({}, '', window.location.pathname);
      }
      if (pendingService) {
        sessionStorage.removeItem('pendingService');
      }
      
      // Create new conversation and send initial message with template context
      const startWithTemplate = async () => {
        try {
          
          // Create the conversation
          const data = await api.post('/chat/conversations');
          const newConvId = data.conversation_id;
          
          // Send the first message BEFORE navigating
          // This ensures the Gatekeeper sees the template context immediately
          await api.post(`/chat/conversations/${newConvId}/messages`, {
            content: `I'd like to get started with: ${templateId}`,
            message_type: 'text',
          });
          
          // Now navigate — conversation already has the message + Gatekeeper will be responding
          await loadConversations();
          navigate(`/chat/${newConvId}`);
        } catch (error) {
          console.error('Failed to start with template:', error);
        } finally {
          setLoading(false);
        }
      };
      
      startWithTemplate();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, conversationId]);

  // Load specific conversation when ID changes
  useEffect(() => {
    if (conversationId) {
      loadConversation(conversationId);
    } else {
      setCurrentConversation(null);
      setMessages([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conversationId]);

  // Auto-focus input on desktop when conversation loads
  useEffect(() => {
    if (conversationId && !loading) {
      // Only auto-focus on desktop (> 768px) to avoid forcing keyboard on mobile
      const isDesktop = window.innerWidth > 768;
      if (isDesktop && inputRef.current) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          inputRef.current?.focus();
        }, 100);
      }
    }
  }, [conversationId, loading]);

  // Scroll to bottom when messages change
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  // Track last message ID for polling
  useEffect(() => {
    if (messages.length > 0) {
      const lastMsg = messages[messages.length - 1];
      if (typeof lastMsg.id === 'number') lastMessageIdRef.current = lastMsg.id;
    }
  }, [messages]);

  // Poll for new messages every 5 seconds when conversation is open
  useEffect(() => {
    if (!conversationId) return;

    const pollMessages = async () => {
      // Don't poll while sending or loading
      if (sending || loading) return;
      
      const lastId = lastMessageIdRef.current;
      if (!lastId) return;

      try {
        const newMessages = await api.get(
          `/chat/conversations/${conversationId}/messages?after=${lastId}`
        );
        if (newMessages && newMessages.length > 0) {
          setMessages((prev) => {
            const existingIds = new Set(prev.map(m => m.id));
            const unique = newMessages.filter(m => !existingIds.has(m.id));
            if (unique.length !== newMessages.length) {
              console.warn('[POLL DEDUP] Filtered', newMessages.length - unique.length, 'duplicates. lastRef:', lastMessageIdRef.current);
            }
            return unique.length > 0 ? [...prev, ...unique] : prev;
          });
	}
      } catch (error) {
        // Silently fail polling - don't spam console
        if (error.status !== 401) {
          console.debug('Poll failed:', error);
        }
      }
    };

    const interval = setInterval(pollMessages, 10000);
    return () => clearInterval(interval);
  }, [conversationId, sending, loading]);

  // Paste handler for images
  useEffect(() => {
    const handlePaste = (e) => {
      const items = Array.from(e.clipboardData?.items || []);
      const imageItems = items.filter(item => item.type.startsWith('image/'));
      if (imageItems.length === 0) return;
      const files = imageItems.map(item => item.getAsFile()).filter(Boolean);
      setPendingFiles(prev => [...prev, ...files].slice(0, 5));
    };
    window.addEventListener('paste', handlePaste);
    return () => window.removeEventListener('paste', handlePaste);
  }, []);

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files || []);
    setPendingFiles(prev => [...prev, ...files].slice(0, 5));
    e.target.value = '';
  };

  const removePendingFile = (index) => {
    setPendingFiles(prev => prev.filter((_, i) => i !== index));
  };

  const uploadPendingFiles = async () => {
    const effectiveFolderIdCheck = activeFolderId
      ? String(activeFolderId)
      : currentConversation?.folder_id
        ? String(currentConversation.folder_id)
        : null;
    if (pendingFiles.length === 0 || (!conversationId && !effectiveFolderIdCheck)) return;
    
    const formData = new FormData();
    pendingFiles.forEach(file => {
      formData.append('files', file);
    });
    
    // Respect folder context - same logic as RightSidebar
    const effectiveFolderId = activeFolderId
      ? String(activeFolderId)
      : currentConversation?.folder_id
        ? String(currentConversation.folder_id)
        : null;
    
    if (effectiveFolderId) {
      formData.append('folder_id', effectiveFolderId);
    } else {
      if (conversationId) { formData.append('conversation_id', conversationId); } else { console.error('Cannot upload: no conversation or folder context'); return; }
    }

    try {
      const response = await fetch('/api/files/upload', {
        method: 'POST',
        credentials: 'include',
        body: formData
      });
      
      if (response.ok) {
        setPendingFiles([]);
        setFileRefreshTrigger(prev => prev + 1);
      } else {
        const error = await response.json();
        console.error('File upload failed:', error.detail);
      }
    } catch (error) {
      console.error('File upload failed:', error);
    }
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadConversations = async () => {
    try {
      const data = await api.get('/chat/conversations');
      setConversations(data);
    } catch (error) {
      console.error('Failed to load conversations:', error);
    }
  };

  const loadFolders = async () => {
    try {
      const data = await api.get('/folders');
      setFolders(Array.isArray(data) ? data : (data.folders || []));
    } catch (error) {
      console.error('Failed to load folders:', error);
    }
  };

  // Domain management
  const loadDomains = useCallback(async () => {
    setDomainsLoading(true);
    try {
      const data = await api.get('/api/customer/domains');
      setDomains(data || []);
    } catch (error) {
      console.error('Failed to load domains:', error);
      setDomains([]);
    } finally {
      setDomainsLoading(false);
    }
  }, []);

  // Sites management
  const loadSites = useCallback(async () => {
    setSitesLoading(true);
    try {
      const data = await api.get('/api/customer/sites');
      setSites(data || []);
    } catch (error) {
      console.error('Failed to load sites:', error);
      setSites([]);
    } finally {
      setSitesLoading(false);
    }
  }, []);

  // Documents management
  const loadDocuments = useCallback(async () => {
    setDocumentsLoading(true);
    setDocumentsError(null);
    try {
      const data = await api.get('/api/customer/documents');
      setDocuments(data || []);
    } catch (error) {
      console.error('Failed to load documents:', error);
      setDocumentsError('Failed to load documents');
    } finally {
      setDocumentsLoading(false);
    }
  }, []);

  const handleModeChange = useCallback((newMode, opts) => {
    setMode(newMode);
    try { localStorage.setItem('tim-sidebar-mode', newMode); } catch {}
    if (newMode === 'domains') {
      loadDomains().then(() => {
        // Auto-select a domain by name if requested (e.g. from SitePanel linked domain click)
        if (opts?.domainName) {
          // domains state updates async — use a microtask to read the freshest state
          setTimeout(() => {
            setDomains(prev => {
              const match = prev.find(d => d.domain_name === opts.domainName);
              if (match) {
                setSelectedDomainId(match.id);
                setDnsPanelOpen(window.innerWidth > 768);
              }
              return prev;
            });
          }, 0);
        }
      });
    }
    if (newMode === 'sites') loadSites();
    if (newMode === 'documents') loadDocuments();
  }, [loadDomains, loadSites, loadDocuments]);

  // Load data when starting in domains/sites/documents mode
  useEffect(() => {
    if (mode === 'domains') loadDomains();
    if (mode === 'sites') loadSites();
    if (mode === 'documents') loadDocuments();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const selectedDomain = domains.find((d) => d.id === selectedDomainId) || null;
  const selectedSite = sites.find((s) => s.id === selectedSiteId) || null;
  const selectedDoc = documents.find((d) => d.job_id === selectedDocId) || null;

  // "Get a Domain" handler — switch to conversations + seed input
  const handleGetDomain = useCallback((siteId, conversationId) => {
    setMode('conversations');
    try { localStorage.setItem('tim-sidebar-mode', 'conversations'); } catch {}
    const seed = `I'd like to get a domain for my site ${siteId}`;
    if (conversationId) {
      navigate(`/chat/${conversationId}`);
    } else {
      navigate('/chat');
    }
    // Defer input seeding to next tick so the conversation view mounts first
    setTimeout(() => setInput(seed), 50);
  }, [navigate]);

  // Open conversation handler — switch to conversations mode + navigate
  const handleOpenConversation = useCallback((conversationId) => {
    setMode('conversations');
    try { localStorage.setItem('tim-sidebar-mode', 'conversations'); } catch {}
    if (conversationId) {
      navigate(`/chat/${conversationId}`);
    } else {
      navigate('/chat');
    }
  }, [navigate]);

  const loadConversation = async (id) => {
    setLoading(true);
    try {
      const data = await api.get(`/chat/conversations/${id}`);
      setCurrentConversation(data);
      setMessages(data.messages || []);
    } catch (error) {
      console.error('Failed to load conversation:', error);
      navigate('/chat');
    } finally {
      setLoading(false);
    }
  };

  // Lightweight message refresh — updates messages without tearing down the UI.
  // Use instead of loadConversation when the component tree should stay mounted
  // (preserves React state, refs, scroll position).
  const refreshMessages = async (id) => {
    try {
      const data = await api.get(`/chat/conversations/${id || conversationId}`);
      setCurrentConversation(data);
      setMessages(data.messages || []);
    } catch (error) {
      console.warn('Message refresh failed:', error);
    }
  };

  const createConversation = async () => {
    try {
      const data = await api.post('/chat/conversations');
      await loadConversations();
      setActiveFolderId(null);
      setActiveFolderName(null);
      navigate(`/chat/${data.conversation_id}`);
    } catch (error) {
      console.error('Failed to create conversation:', error);
    }
  };

  // Handle successful payment redirect from Stripe
  const handlePaymentSuccess = async (sessionId) => {
    // Clear query params immediately using replaceState (no re-render)
    const cleanUrl = window.location.pathname;
    window.history.replaceState({}, '', cleanUrl);

    if (!conversationId) {
      // If no conversation ID in URL, redirect to most recent conversation
      try {
        const convs = await api.get('/chat/conversations');
        if (convs.length > 0) {
          navigate(`/chat/${convs[0].id}`);
          // The payment will be verified on next load since we cleared params
        }
      } catch (error) {
        console.error('Failed to find conversation:', error);
      }
      return;
    }

    setSending(true);
    try {
      const data = await api.post(`/chat/conversations/${conversationId}/verify-payment`, {
        session_id: sessionId,
      });

      if (data.success) {
        // Reload conversation to show the confirmation message
        await refreshMessages();
        loadConversations(); // Refresh sidebar
      } else {
        // Show error - reload to see any error message stored
        await refreshMessages();
      }
    } catch (error) {
      console.error('Failed to verify payment:', error);
      // Reload conversation anyway to see current state
      await refreshMessages();
    } finally {
      setSending(false);
    }
  };

  const sendMessage = async (e) => {
    e.preventDefault();
    if (!input.trim() || sending || !conversationId) return;

    const messageText = input.trim();
    setInput('');
    setSending(true);

    // Upload pending files first
    if (pendingFiles.length > 0) {
      await uploadPendingFiles();
    }

    // Optimistically add customer message
    const tempCustomerMsg = {
      id: 'temp_' + Date.now(),
      role: 'customer',
      content: messageText,
      message_type: 'text',
      metadata: {},
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempCustomerMsg]);

    try {
      const data = await api.post(`/chat/conversations/${conversationId}/messages`, {
        content: messageText,
      });

      // Replace temp message with real one and add response
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempCustomerMsg.id);
        return [...filtered, data.customer_message, data.gatekeeper_response];
      });

      // Refresh conversations list to update preview
      loadConversations();
    } catch (error) {
      console.error('Failed to send message:', error);
      // Remove optimistic message on error
      setMessages((prev) => prev.filter((m) => m.id !== tempCustomerMsg.id));
      setInput(messageText); // Restore input
    } finally {
      setSending(false);
      // Re-focus input on desktop after send attempt
      setTimeout(() => {
        if (window.innerWidth > 768 && inputRef.current) {
          inputRef.current.focus();
        }
      }, 150);
    }
  };

  const handleScopeCardAction = async (action, messageId) => {
    if (!conversationId) return;

    setSending(true);
    try {
      if (action === 'approve') {
        const result = await api.post(`/chat/conversations/${conversationId}/scope-card/approve`);
        
        // Log unknown actions for diagnostics — do not break UI
        const knownActions = new Set([
          'add_payment_method', 'job_created', 'stripe_existing',
          'await_usdc_payment', 'payment_confirmed', 'await_payment_method_selection',
          'wallet_required', 'payment_failed', 'denied', 'validation_failed',
          'already_paid', 'already_processing', 'unknown_outcome', 'rail_mismatch',
          'declined', 'order_created', 'usdc_in_flight', 'usdc_unavailable'
        ]);
        const returnedAction = result?.action;
        if (returnedAction && !knownActions.has(returnedAction)) {
          console.warn('[approve] Unknown action from backend:', returnedAction);
        }
        
        // All actions: reload conversation to render stored messages (including
        // idempotency messages from the backend guard).
        await refreshMessages();

        // Return result so ScopeCard can decide whether to release the lock
        return result;
        
      } else if (action === 'decline') {
        const data = await api.post(`/chat/conversations/${conversationId}/scope-card/decline`);
        setMessages((prev) => [...prev, data.gatekeeper_response]);
      }
    } catch (error) {
      console.error('Failed to process scope card action:', error);
      // S7: browser timeout or network error = unknown outcome
      // Don't say "something went wrong" — money may be in flight
      const isTimeout = error?.status === 0 || error?.message?.includes('timed out');
      const errorMsg = {
        id: 'temp_' + Date.now(),
        role: 'gatekeeper',
        content: isTimeout
          ? "We're confirming your payment — you won't be charged twice. I'll update you here."
          : `Something went wrong: ${error.message || 'Please try again.'}`,
        message_type: 'text',
        metadata: {},
        created_at: new Date().toISOString(),
      };
      setMessages((prev) => [...prev, errorMsg]);
      // After a timeout, refresh to see the real state
      if (isTimeout) {
        try { await refreshMessages(); } catch(e) { /* ignore */ }
      }
      // Return null so ScopeCard keeps the lock on timeout
      return isTimeout ? null : { action: 'error' };
    } finally {
      setSending(false);
    }
  };

  // Handle domain selection from clickable domain options
  const handleDomainSelect = async (domain) => {
    if (sending || !conversationId) return;

    setSending(true);

    // Optimistically add customer message
    const tempCustomerMsg = {
      id: 'temp_' + Date.now(),
      role: 'customer',
      content: domain,
      message_type: 'text',
      metadata: {},
      created_at: new Date().toISOString(),
    };
    setMessages((prev) => [...prev, tempCustomerMsg]);

    try {
      const data = await api.post(`/chat/conversations/${conversationId}/messages`, {
        content: domain,
      });

      // Replace temp message with real one and add response
      setMessages((prev) => {
        const filtered = prev.filter((m) => m.id !== tempCustomerMsg.id);
        return [...filtered, data.customer_message, data.gatekeeper_response];
      });

      loadConversations();
    } catch (error) {
      console.error('Failed to send domain selection:', error);
      setMessages((prev) => prev.filter((m) => m.id !== tempCustomerMsg.id));
    } finally {
      setSending(false);
    }
  };

  const toggleSidebar = () => {
    setSidebarOpen((prev) => {
      // Blur input when opening sidebar to dismiss mobile keyboard
      if (!prev) {
        inputRef.current?.blur();
      }
      return !prev;
    });
  };

  // Swipe gesture handling for sidebar
  const touchStartRef = useRef({ x: 0, y: 0 });
  const handleTouchStart = (e) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY
    };
  };

  const handleTouchEnd = (e) => {
    const deltaX = e.changedTouches[0].clientX - touchStartRef.current.x;
    const deltaY = e.changedTouches[0].clientY - touchStartRef.current.y;
    const startX = touchStartRef.current.x;
    
    // Only register as swipe if horizontal movement > vertical and > 50px
    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      // Swipe right from left edge opens left sidebar
      if (deltaX > 0 && startX < 80 && !sidebarOpen && !rightSidebarOpen) {
        inputRef.current?.blur();
        setSidebarOpen(true);
      }
      // Swipe left closes left sidebar when open
      else if (deltaX < 0 && sidebarOpen) {
        setSidebarOpen(false);
      }
      // Swipe left from right edge opens files panel
      else if (deltaX < 0 && startX > window.innerWidth - 40 && !rightSidebarOpen && !sidebarOpen) {
        setRightSidebarOpen(true);
      }
      // Swipe right closes files panel when open
      else if (deltaX > 0 && rightSidebarOpen) {
        setRightSidebarOpen(false);
      }
    }
  };

  return (
    <div 
      className="chat-layout"
      onTouchStart={handleTouchStart}
      onTouchEnd={handleTouchEnd}
    >
      <div className="edge-indicator edge-left" onClick={() => { if (!sidebarOpen) setSidebarOpen(true); }}>‹</div>
      <div className="edge-indicator edge-right" onClick={() => { if (!rightSidebarOpen) setRightSidebarOpen(true); }}>›</div>
      <ChatSidebar
        conversations={conversations}
        currentId={conversationId ? parseInt(conversationId) : null}
        onNewConversation={createConversation}
        isOpen={sidebarOpen}
        onToggle={toggleSidebar}
        onRefresh={loadConversations}
        onFolderSelect={(folderId, folderName) => {
          setActiveFolderId(folderId);
          setActiveFolderName(folderName);
        }}
        onConversationRenamed={(convId, newTitle) => {
          if (currentConversation && currentConversation.id === convId) {
            setCurrentConversation(prev => ({ ...prev, title: newTitle }));
          }
        }}
        mode={mode}
        onModeChange={handleModeChange}
        domains={domains}
        domainsLoading={domainsLoading}
        selectedDomainId={selectedDomainId}
        onDomainSelect={(id) => {
          setSelectedDomainId(id);
          setDnsPanelOpen(window.innerWidth > 768);
          if (window.innerWidth <= 768) setSidebarOpen(false);
        }}
        sites={sites}
        sitesLoading={sitesLoading}
        selectedSiteId={selectedSiteId}
        onSiteSelect={(id) => {
          setSelectedSiteId(id);
          setSitePanelOpen(window.innerWidth > 768);
          if (window.innerWidth <= 768) setSidebarOpen(false);
        }}
        documents={documents}
        documentsLoading={documentsLoading}
        documentsError={documentsError}
        selectedDocId={selectedDocId}
        onDocSelect={(id) => {
          setSelectedDocId(id);
          setDocPanelOpen(window.innerWidth > 768);
          if (window.innerWidth <= 768) setSidebarOpen(false);
        }}
        onDocRetry={loadDocuments}
      />

      <div
        className={`chat-main ${sidebarOpen ? '' : 'sidebar-closed'}`}
        onClick={() => {
          if (window.innerWidth <= 768) {
            if (sidebarOpen) setSidebarOpen(false);
            if (rightSidebarOpen) setRightSidebarOpen(false);
          }
        }}
      >
        {mode === 'domains' ? (
          <DomainDetail domain={selectedDomain} />
        ) : mode === 'sites' ? (
          <SiteDetail site={selectedSite} onGetDomain={handleGetDomain} />
        ) : mode === 'documents' ? (
          <DocumentDetail doc={selectedDoc} onOpenConversation={handleOpenConversation} />
        ) : loading ? (
          <div className="chat-loading" style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
            <div className="loading-spinner"></div>
            <p style={{ marginTop: '1rem', color: 'var(--text-secondary)', textAlign: 'center' }}>Setting up your project...</p>
          </div>
        ) : !conversationId ? (
          <div className="chat-empty">
            <div className="chat-empty-content">
              <h2>Start a conversation</h2>
              <div className="chat-onboarding-blurb">
                <p><span>&larr;</span> Conversations live on the right</p>
		<p>(rename them and thank me later)</p>
                <p>Add files to your projects on the left <span>&rarr;</span></p>
                <p>Build your ideas with the chat in the middle</p>
                <p>Simple.</p>
		<p><Link to="/jobs">Some Examples</Link></p>
              </div>
              <div className="chat-empty-buttons">
                <button onClick={createConversation} className="btn btn-primary">
                  New Conversation
                </button>
                {conversations.length > 0 && window.innerWidth <= 768 && (
                  <button 
                    onClick={() => setSidebarOpen(true)} 
                    className="btn btn-secondary"
                  >
                    Continue Project
                  </button>
                )}
              </div>
            </div>
          </div>
        ) : (
          <>
            <div className="chat-messages">
              {messages.length === 0 ? (
                <div className="chat-welcome">
                  {/* BUG 5 FIX: Removed "Gatekeeper" reference */}
                  <h3>👋 Welcome to The Idea Machine!</h3>
                  <p>
                    Tell us what you're looking to build—a website, an app, an
                    automation, whatever you need. We'll help figure out the
                    details and get it built for you.
                  </p>
                </div>
              ) : (
                messages.map((message, index) => {
                  // For USDC payment_prompt, check if payment is confirmed
                  // (a message exists after this one in the list)
                  const isUsdcPayment = message.message_type === 'payment_prompt' && 
                                        message.metadata?.payment_type === 'usdc';
                  const hasNextMessage = index < messages.length - 1;
                  const isPaymentConfirmed = false;// isUsdcPayment && hasNextMessage;
                  
                  return (
                    <MessageBubble
                      key={message.id}
                      message={message}
                      onScopeCardAction={handleScopeCardAction}
                      onDomainSelect={handleDomainSelect}
                      disabled={sending}
                      conversationId={conversationId}
                      isPaymentConfirmed={isPaymentConfirmed}
                      onPaymentConfirmed={async () => {
                        try {
                          const data = await api.get(`/chat/conversations/${conversationId}`);
                          setMessages(data.messages || []);
                        } catch (e) {
                          console.warn('Payment confirmed refresh failed:', e);
                        }
                      }}
                    />
                  );
                })
              )}
              {sending && (
                <div className="message-bubble gatekeeper">
                  <div className="typing-indicator">
                    <span></span>
                    <span></span>
                    <span></span>
                  </div>
                </div>
              )}
              <div ref={messagesEndRef} />
            </div>

            {pendingFiles.length > 0 && (
              <div className="paste-preview">
                {pendingFiles.map((file, i) => (
                  <div className="paste-chip" key={i}>
                    {file.type?.startsWith('image/') ? (
                      <img 
                        src={URL.createObjectURL(file)} 
                        alt="" 
                        onError={(e) => { e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <span className="file-icon-preview">📄</span>
                    )}
                    <span>{file.name || 'pasted image'}</span>
                    <button 
                      className="paste-chip-remove" 
                      onClick={() => removePendingFile(i)}
                    >
                      ×
                    </button>
                  </div>
                ))}
              </div>
            )}

            <form onSubmit={sendMessage} className="chat-input-form">
              <div className="chat-input-actions">
                <button
                  type="button"
                  className="sidebar-toggle-mobile"
                  onClick={toggleSidebar}
                  aria-label="Toggle conversations"
                >
                  ☰
                </button>
                <button
                  type="button"
                  className="files-toggle-mobile"
                  onClick={() => setRightSidebarOpen(!rightSidebarOpen)}
                  aria-label="Toggle files panel"
                >
                  📁
                </button>
                <button
                  type="button"
                  className="paperclip-btn"
                  onClick={() => fileInputRef.current?.click()}
                  title="Attach file"
                >
                  📎
                </button>
              </div>
              <input
                ref={fileInputRef}
                type="file"
                style={{ display: 'none' }}
                accept="image/jpeg,image/png,image/gif,image/webp"
                multiple
                onChange={handleFileSelect}
              />
              <textarea
                ref={inputRef}
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && !e.shiftKey) {
                    e.preventDefault();
                    if (input.trim() && !sending) {
                      sendMessage(e);
                    }
                  }
                }}
                placeholder="Type your message..."
                className="chat-input"
                disabled={sending}
                rows={1}
              />
              <button
                type="submit"
                className="chat-send-btn"
                disabled={!input.trim() || sending}
              >
                Send
              </button>
            </form>
          </>
        )}
      </div>

      {mode === 'domains' ? (
        selectedDomain && dnsPanelOpen && (
          <div className="chat-right-sidebar open">
            <DomainDnsPanel
              domainId={selectedDomain.id}
              domainName={selectedDomain.domain_name}
              onClose={() => setDnsPanelOpen(false)}
            />
          </div>
        )
      ) : mode === 'sites' ? (
        selectedSite && sitePanelOpen && (
          <div className="chat-right-sidebar open">
            <SitePanel
              site={selectedSite}
              onClose={() => setSitePanelOpen(false)}
              onModeChange={handleModeChange}
              onGetDomain={handleGetDomain}
            />
          </div>
        )
      ) : mode === 'documents' ? (
        selectedDoc && docPanelOpen && (
          <div className="chat-right-sidebar open">
            <DocumentPanel
              doc={selectedDoc}
              onClose={() => setDocPanelOpen(false)}
              onModeChange={handleModeChange}
              onOpenConversation={handleOpenConversation}
            />
          </div>
        )
      ) : (
        <>
          {rightSidebarOpen && (
            <div className="right-sidebar-overlay visible" onClick={() => setRightSidebarOpen(false)} />
          )}
          <RightSidebar
            isOpen={rightSidebarOpen}
            onClose={() => setRightSidebarOpen(false)}
            conversationId={conversationId}
            folderId={
              activeFolderId
                ? String(activeFolderId)
                : currentConversation?.folder_id
                  ? String(currentConversation.folder_id)
                  : null
            }
            refreshTrigger={fileRefreshTrigger}
            folderName={
              activeFolderName ||
              ((activeFolderId || currentConversation?.folder_id)
                ? (folders.find(f => f.id === (activeFolderId || currentConversation?.folder_id))?.name || null)
                : null)
            }
            conversationTitle={
              currentConversation?.title ||
              (currentConversation?.created_at
                ? new Date(currentConversation.created_at).toLocaleDateString()
                : null)
            }
          />
        </>
      )}
    </div>
  );
}

export default Chat;

